import { useState, type ReactNode } from 'react';
import { SiYoutube, SiYoutubeshorts } from 'react-icons/si';
import Tooltip from '@/components/Tooltip';
import { PopoverShell } from '@/components/PopoverShell';
import { getVideoEmbedUrl, type VideoEmbed } from '@/lib/utils/videoEmbed';
import { useI18nText } from '@/lib/utils/i18nText';
import { toHumanizedDate } from '@/lib/utils/timezone';
import { EmptyDash } from '../../utils';
import type { Influencer } from '../../types';

/** Maps this app's channel-type codes to the `type` string(s) `getVideoEmbedUrl`
 *  expects, trying each candidate in turn — a channel type can map to more than
 *  one post kind (e.g. a Twitch URL could be a VOD or a clip). Exported for reuse
 *  by other click-to-preview cells (e.g. `VideoTitleWithPreview`). */
export function resolveVideoEmbed(channelType: string, url?: string): VideoEmbed | null {
  if (!url) return null;
  const candidates: string[] =
    channelType === 'youtube' ? ['youtube_video'] :
    channelType === 'twitch' ? ['twitch_video', 'twitch_clip'] :
    channelType === 'tiktok' ? ['tiktok_video'] :
    channelType === 'instagram' ? ['instagram_video', 'instagram_photo'] :
    channelType === 'bilibili' ? ['bilibili_video'] :
    [];
  for (const type of candidates) {
    const embed = getVideoEmbedUrl({ type, url });
    if (embed) return embed;
  }
  return null;
}

/** LVA 날짜 링크 + 비디오 제목 Tooltip — any URL the shared embed utility can
 *  resolve (YouTube/Twitch/TikTok/Instagram/Bilibili) opens a preview popover
 *  (+ an explicit "open in new tab" link inside it) instead of navigating away
 *  immediately; anything else keeps the plain external-link behavior. */
export function LvaLink({ url, title, date, channelType }: { url?: string; title?: string; date?: string; channelType: string }) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  const embed = resolveVideoEmbed(channelType, url);
  const dateText = <span className="lva-card-date">{toHumanizedDate(date)}</span>;

  if (embed) {
    // No thumbnail image — a click-to-preview popover is enough on its own
    // (and no platform besides YouTube has a public no-auth thumbnail source
    // anyway, so this keeps every platform's trigger visually the same).
    // essential: this is the only visible cue that clicking opens a preview
    // instead of navigating away — must stay visible even with Tutorial Mode
    // off. Tooltip nests *inside* the button (not wrapping it) so the button
    // itself stays the ref-forwarding element PopoverShell's Radix trigger needs.
    const trigger = (
      <button type="button" className="lva-video-trigger" aria-label={title || t('Preview video', '영상 미리보기')}>
        <Tooltip essential content={t('Click to preview the video', '클릭하면 영상 미리보기가 가능합니다')}>
          {dateText}
        </Tooltip>
      </button>
    );
    return (
      <PopoverShell
        open={open}
        onOpenChange={setOpen}
        trigger={trigger}
        side="bottom"
        align="end"
        width={embed.aspect === 'portrait' ? 220 : 260}
      >
        <div className="lva-video-popover">
          {title && <div className="lva-video-popover-title">{title}</div>}
          <div className={`lva-video-popover-embed${embed.aspect === 'portrait' ? ' lva-video-popover-embed--portrait' : ''}`}>
            <iframe
              src={embed.url}
              title={title || 'Video preview'}
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          {url && (
            <a href={url} target="_blank" rel="noopener noreferrer" className="lva-video-popover-link">
              {t('Open in new tab', '새 창에서 열기')} <i className="fa fa-external-link" aria-hidden />
            </a>
          )}
        </div>
      </PopoverShell>
    );
  }

  const link = url
    ? <a href={url} target="_blank" rel="noopener noreferrer" className="lva-card-link">{dateText}</a>
    : dateText;
  // essential: the video title identifies *which* post the date refers to (real
  // content, not a usage hint) — must stay visible even with Tutorial Mode off.
  return title ? <Tooltip essential content={title}>{link}</Tooltip> : link;
}

/** Single row: [icon] [label] [date/date-link] — visually grouped as a card */
function LvaCard({ icon, label, url, title, date, channelType }: {
  icon: ReactNode;
  label: string;
  url?: string;
  title?: string;
  date?: string;
  channelType: string;
}) {
  return (
    <div className="lva-card">
      <span className="lva-card-icon" aria-hidden>{icon}</span>
      <div className="lva-card-content">
        <span className="lva-card-label">{label}</span>
        <LvaLink url={url} title={title} date={date} channelType={channelType} />
      </div>
    </div>
  );
}

type LvaPostRef = { publishedAt?: string; title?: string; url?: string };

/**
 * Last-activity cell rendered from already-resolved primitives (no `Influencer`
 * shape required) so pages with different row models (e.g. candidates' history
 * rows) can share the exact same "Latest Video Activity"/"Last Activity" style.
 * Twitch: Stream + Video post · YouTube: normal/short/live (or Latest) · others: Latest.
 */
export function LastVideoActivity({
  channelType,
  latestPost,
  latestPostByType,
  lastStreamAt,
  showEmptyDash = true,
}: {
  channelType: string;
  latestPost?: LvaPostRef;
  latestPostByType?: {
    normal?: LvaPostRef | null;
    short?: LvaPostRef | null;
    live?: LvaPostRef | null;
  };
  lastStreamAt?: string;
  showEmptyDash?: boolean;
}) {
  if (channelType === 'twitch') {
    const hasStream = !!lastStreamAt;
    const hasVideo = !!latestPost?.publishedAt;
    if (!hasStream && !hasVideo) return showEmptyDash ? <EmptyDash /> : null;
    return (
      <div className="lva-cell">
        {hasStream && <LvaCard icon={<i className="fa fa-broadcast-tower" />} label="Stream" date={lastStreamAt} channelType={channelType} />}
        {hasVideo && <LvaCard icon={<i className="fa fa-play-circle-o" />} label="Video post" url={latestPost?.url} title={latestPost?.title} date={latestPost?.publishedAt} channelType={channelType} />}
      </div>
    );
  }

  if (channelType === 'youtube') {
    const types = ['normal', 'short', 'live'] as const;
    // YouTube brand icons (inherit current text color via SVG currentColor)
    const typeIcons: Record<typeof types[number], ReactNode> = {
      normal: <SiYoutube size={11} />,
      short: <SiYoutubeshorts size={11} />,
      live: <i className="fa fa-broadcast-tower" />,
    };
    const typeLabels: Record<typeof types[number], string> = {
      normal: 'Normal',
      short: 'Shorts',
      live: 'Live',
    };
    const hasByType = latestPostByType && types.some((tp) => latestPostByType[tp]?.publishedAt);
    if (!hasByType && !latestPost?.publishedAt) return showEmptyDash ? <EmptyDash /> : null;
    if (hasByType && latestPostByType) {
      return (
        <div className="lva-cell">
          {types.map((tp) => {
            const post = latestPostByType[tp];
            if (!post?.publishedAt) return null;
            return (
              <LvaCard
                key={tp}
                icon={typeIcons[tp]}
                label={typeLabels[tp]}
                url={post.url}
                title={post.title}
                date={post.publishedAt}
                channelType={channelType}
              />
            );
          })}
        </div>
      );
    }
    return (
      <div className="lva-cell">
        <LvaCard icon={<SiYoutube size={11} />} label="Latest" url={latestPost?.url} title={latestPost?.title} date={latestPost?.publishedAt} channelType={channelType} />
      </div>
    );
  }

  if (!latestPost?.publishedAt) return showEmptyDash ? <EmptyDash /> : null;
  return (
    <div className="lva-cell">
      <LvaCard icon={<i className="fa fa-play-circle-o" />} label="Latest" url={latestPost.url} title={latestPost.title} date={latestPost.publishedAt} channelType={channelType} />
    </div>
  );
}

/** LastVideo 셀 (원본 LastVideo) — `Influencer` 행 모델용 얇은 래퍼. */
export function LastVideoCell({ user, showEmptyDash = true }: { user: Influencer; showEmptyDash?: boolean }) {
  return (
    <LastVideoActivity
      channelType={user.channels?.[0]?.type ?? 'youtube'}
      latestPost={user.latestPost}
      latestPostByType={user.latestPostByType}
      lastStreamAt={user.twitchStats?.lastStreamAt}
      showEmptyDash={showEmptyDash}
    />
  );
}
